import React, { useState, useEffect, useCallback } from 'react';
import {
  RefreshCw,
  Calendar,
  Pause,
  Play,
  X,
  AlertCircle,
  Package,
  Clock,
  DollarSign,
  Eye,
  MoreVertical,
  UserPlus,
  AlertTriangle,
  User,
} from 'lucide-react';
import SmartPagination from '@/components/ui/smart-pagination';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import subscriptionService, {
  Subscription,
  getMedicationRequests,
} from '@/services/subscriptionService';
import { useClinic } from '@/contexts/ClinicContext';
import { useAuth } from '@/contexts/AuthContext';
import { PaginationParams } from '@/lib/types';
import EnrollPatientDialog from '@/components/subscriptions/EnrollPatientDialog';
import CancelSubscriptionDialog from '@/components/subscriptions/CancelSubscriptionDialog';
import SubscriptionDetailSheet from '@/components/subscriptions/SubscriptionDetailSheet';
import { Skeleton } from '@/components/ui/skeleton';
import MetricsCard from '@/components/MetricsCard';

// Subscription Status Badge Component
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  switch (status) {
    case 'active':
      return (
        <Badge
          className='inline-flex w-fit items-center gap-1 border-0 bg-emerald-600 px-2 py-0.5 font-medium text-white hover:bg-emerald-600'
          style={{ fontSize: '0.7rem' }}
        >
          Active
        </Badge>
      );
    case 'paused':
      return (
        <Badge
          className='inline-flex w-fit items-center gap-1 border-0 bg-amber-500 px-2 py-0.5 font-medium text-white hover:bg-amber-500'
          style={{ fontSize: '0.7rem' }}
        >
          Paused
        </Badge>
      );
    case 'cancelled':
    case 'canceled':
      return (
        <Badge
          className='inline-flex w-fit items-center gap-1 border-0 bg-red-600 px-2 py-0.5 font-medium text-white hover:bg-red-600'
          style={{ fontSize: '0.7rem' }}
        >
          Cancelled
        </Badge>
      );
    case 'expired':
      return (
        <Badge
          className='inline-flex w-fit items-center gap-1 border-0 bg-slate-500 px-2 py-0.5 font-medium text-white hover:bg-slate-500'
          style={{ fontSize: '0.7rem' }}
        >
          Expired
        </Badge>
      );
    default:
      return (
        <Badge
          className='inline-flex w-fit items-center gap-1 border-0 bg-slate-500 px-2 py-0.5 font-medium text-white hover:bg-slate-500'
          style={{ fontSize: '0.7rem' }}
        >
          {status}
        </Badge>
      );
  }
};

// Subscription Row Component
const SubscriptionRow: React.FC<{
  subscription: Subscription;
  index: number;
  onStatusUpdate: (id: string, status: string) => void;
  onView: (subscription: Subscription) => void;
  onCancel: (subscription: Subscription) => void;
}> = ({ subscription, index, onStatusUpdate, onView, onCancel }) => {
  const [isUpdating, setIsUpdating] = useState(false);

  const handleStatusUpdate = async (newStatus: string) => {
    setIsUpdating(true);
    try {
      await subscriptionService.updateSubscriptionStatus(subscription.id, newStatus as any);
      onStatusUpdate(subscription.id, newStatus);
      toast({
        title: 'Subscription updated',
        description: `Subscription has been ${newStatus}.`,
        variant: 'success',
      });
    } catch (error: any) {
      toast({
        title: 'Update failed',
        description: error.message || 'Failed to update subscription status.',
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  const getFrequencyText = (frequency: string) => {
    switch (frequency) {
      case 'weekly':
        return 'Weekly';
      case 'monthly':
        return 'Monthly';
      case 'quarterly':
        return 'Quarterly';
      case 'yearly':
        return 'Yearly';
      default:
        return frequency;
    }
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Use helper function to get medication requests from subscriptionMedications
  const medicationRequests = getMedicationRequests(subscription);

  const getMedicationsPreview = () => {
    if (medicationRequests.length === 0) return 'No medications';
    if (medicationRequests.length === 1) return medicationRequests[0].medication;
    return `${medicationRequests[0].medication} +${medicationRequests.length - 1} more`;
  };

  const getTotalAmount = () => {
    if (typeof subscription.prescription?.total_amount === 'number') {
      return subscription.prescription.total_amount;
    }

    const medicationTotal = getMedicationTotal();
    const shippingCostCents = getShippingCostCents();

    if (typeof shippingCostCents === 'number') {
      return medicationTotal + shippingCostCents / 100;
    }

    return medicationTotal;
  };

  const getMedicationTotal = () => {
    if (subscription.amount) {
      return subscription.amount;
    }
    return medicationRequests.reduce((sum, med: any) => {
      const price =
        med.clinicProduct?.base_price_cents || (med.product?.price_per_unit || med.product?.price_plan_cost || 0) * 100 || 0;
      return sum + price / 100;
    }, 0);
  };

  const getShippingCostCents = () => {
    const direct = (subscription as any).shipping_cost_cents;
    const prescriptionCost = subscription.prescription?.shipping_cost_cents;
    const nestedCost = (subscription.prescription as any)?.shipping?.cost_cents;

    if (typeof direct === 'number') return direct;
    if (typeof prescriptionCost === 'number') return prescriptionCost;
    if (typeof nestedCost === 'number') return nestedCost;

    return null;
  };

  const shippingCostCents = getShippingCostCents();
  const hasShippingConfigured =
    typeof shippingCostCents === 'number' ||
    !!subscription.prescription?.shipping_carrier_id ||
    !!subscription.prescription?.shipping_service_id ||
    !!subscription.prescription?.is_free_shipment;

  // Check for payment failures
  const hasPaymentFailure = subscription.last_payment_status === 'failed';

  // Rotate colors for user icons (10 different colors)
  const colors = [
    { bg: 'bg-blue-100', text: 'text-blue-600' },
    { bg: 'bg-purple-100', text: 'text-purple-600' },
    { bg: 'bg-pink-100', text: 'text-pink-600' },
    { bg: 'bg-orange-100', text: 'text-orange-600' },
    { bg: 'bg-amber-100', text: 'text-amber-600' },
    { bg: 'bg-cyan-100', text: 'text-cyan-600' },
    { bg: 'bg-indigo-100', text: 'text-indigo-600' },
    { bg: 'bg-rose-100', text: 'text-rose-600' },
    { bg: 'bg-violet-100', text: 'text-violet-600' },
    { bg: 'bg-sky-100', text: 'text-sky-600' },
  ];
  const colorIndex = index % colors.length;
  const iconColor = colors[colorIndex];

  return (
    <tr className='border-b border-slate-100 transition-colors hover:bg-slate-50/80'>
      <td className='w-12 px-4 py-3 text-slate-500' style={{ fontSize: '0.8rem' }}>
        #{String(index + 1).padStart(3, '0')}
      </td>
      <td className='max-w-[200px] px-4 py-3' style={{ fontSize: '0.8rem' }}>
        <div className='flex items-center gap-3'>
          <div
            className={`h-8 w-8 ${iconColor.bg} flex flex-shrink-0 items-center justify-center rounded-lg`}
          >
            <User className={`h-4 w-4 ${iconColor.text}`} />
          </div>
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className='block max-w-[150px] cursor-default truncate font-medium capitalize text-slate-900'>
                  {subscription.patient?.full_name || ''}
                </span>
              </TooltipTrigger>
              <TooltipContent
                side='top'
                className='max-w-[300px] border-black bg-black text-xs text-white [&>svg]:fill-black'
              >
                {subscription.patient?.full_name || ''}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </td>
      <td className='px-4 py-3' style={{ fontSize: '0.8rem' }}>
        <div className='flex flex-col gap-0.5'>
          <div className='font-medium text-slate-900'>
            {subscription.program?.title || 'No Program'}
          </div>
          {subscription.program?.subtitle && (
            <div className='text-xs text-slate-500'>{subscription.program.subtitle}</div>
          )}
        </div>
      </td>
      <td className='px-4 py-3' style={{ fontSize: '0.8rem' }}>
        <div className='flex flex-col gap-0.5'>
          <div className='font-medium text-slate-900'>
            {getMedicationsPreview()}
          </div>
          <div className='text-xs text-slate-500'>
            {medicationRequests.length || 0} medication
            {medicationRequests.length !== 1 ? 's' : ''}
          </div>
        </div>
      </td>
      <td className='px-4 py-3' style={{ fontSize: '0.8rem' }}>
        <div className='flex items-center gap-2'>
          <Calendar className='h-4 w-4 text-slate-400' />
          <span className='text-slate-700'>
            {getFrequencyText(subscription.frequency)}
          </span>
        </div>
      </td>
      <td className='px-4 py-3' style={{ fontSize: '0.8rem' }}>
        <div className='flex items-center gap-2'>
          <Clock className='h-4 w-4 text-slate-400' />
          <span className='text-slate-700'>
            {formatDate(subscription.next_run_date)}
          </span>
        </div>
      </td>
      <td className='px-4 py-3' style={{ fontSize: '0.8rem' }}>
        <div className='flex flex-col gap-1.5'>
          <div className='flex flex-wrap items-center gap-1.5'>
            <StatusBadge status={subscription.status} />
            {hasPaymentFailure && (
              <Badge
                className='inline-flex w-fit items-center gap-1 border-0 bg-red-600 px-2 py-0.5 font-medium text-white hover:bg-red-600'
                style={{ fontSize: '0.7rem' }}
              >
                <AlertTriangle className='h-3 w-3' />
                Payment Failed
              </Badge>
            )}
          </div>
          <div className='flex items-center gap-1 text-xs text-slate-500'>
            <DollarSign className='h-3 w-3' />${getTotalAmount().toFixed(2)}/refill
          </div>
          {hasShippingConfigured && (
            <div className='text-xs text-slate-400'>
              {typeof shippingCostCents === 'number' && shippingCostCents > 0
                ? `Includes $${(shippingCostCents / 100).toFixed(2)} shipping`
                : 'Free shipping'}
            </div>
          )}
        </div>
      </td>
      <td
        className='sticky right-0 w-12 px-2 py-3'
        style={{
          background:
            'linear-gradient(to right, transparent 0%, white 20%, white 100%)',
        }}
      >
        <div className='flex items-center justify-end'>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' size='icon' className='h-8 w-8 hover:bg-slate-100'>
                <MoreVertical className='h-4 w-4 text-slate-500' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end' className='w-48'>
              <DropdownMenuItem
                onClick={() => onView(subscription)}
                className='cursor-pointer'
                style={{ fontSize: '13px' }}
              >
                <Eye className='mr-2 h-4 w-4 text-slate-500' />
                View Details
              </DropdownMenuItem>
              {subscription.status === 'active' && !(subscription as any).guidedOrder && (
                <DropdownMenuItem
                  onClick={() => handleStatusUpdate('paused')}
                  disabled={isUpdating}
                  className='cursor-pointer'
                  style={{ fontSize: '13px' }}
                >
                  <Pause className='mr-2 h-4 w-4 text-slate-500' />
                  Pause Subscription
                </DropdownMenuItem>
              )}
              {subscription.status === 'paused' && !(subscription as any).guidedOrder && (
                <DropdownMenuItem
                  onClick={() => handleStatusUpdate('active')}
                  disabled={isUpdating}
                  className='cursor-pointer'
                  style={{ fontSize: '13px' }}
                >
                  <Play className='mr-2 h-4 w-4 text-slate-500' />
                  Resume Subscription
                </DropdownMenuItem>
              )}
              {['active', 'paused'].includes(subscription.status) && !(subscription as any).guidedOrder && (
                <DropdownMenuItem
                    onClick={() => onCancel(subscription)}
                    disabled={isUpdating}
                    className='cursor-pointer text-red-600 focus:bg-red-50 focus:text-red-600'
                    style={{ fontSize: '13px' }}
                  >
                    <X className='mr-2 h-4 w-4' />
                    Cancel Subscription
                  </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </td>
    </tr>
  );
};

// Main Subscriptions Component
const Subscriptions: React.FC = () => {
  const { clinic, isGuided } = useClinic();
  const { user } = useAuth();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSubscription, setSelectedSubscription] = useState<Subscription | null>(null);
  const [filter, setFilter] = useState<'all' | 'active' | 'paused' | 'cancelled'>('all');

  // Dialog states
  const [showEnrollDialog, setShowEnrollDialog] = useState(false);
  const [showDetailSheet, setShowDetailSheet] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [subscriptionToCancel, setSubscriptionToCancel] = useState<Subscription | null>(null);

  // Pagination state (using PaginationParams for consistency)
  const [pagination, setPagination] = useState<PaginationParams>({
    page: 1,
    limit: 10,
    totalItems: 0,
    totalPages: 0,
    itemsPerPage: 10,
  });

  // Stats (counts by status) - fetched separately for accurate totals
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    paused: 0,
    cancelled: 0,
  });

  const fetchSubscriptions = useCallback(async () => {
    if (!clinic?.id) return;

    try {
      setLoading(true);
      setError(null);

      const statusParam =
        filter === 'all' ? undefined : (filter === 'cancelled' ? 'cancelled' : filter);

      const response = await subscriptionService.getClinicSubscriptions(
        clinic.id,
        pagination.page ?? 1,
        pagination.limit ?? 10,
        statusParam
      );

      setSubscriptions(response.subscriptions);
      const limit = pagination.limit ?? 10;
      setPagination(prev => ({
        ...prev,
        totalItems: response.pagination?.total ?? 0,
        totalPages: response.pagination?.totalPages ?? 0,
        itemsPerPage: limit,
      }));
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
      setError('Failed to load subscriptions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [clinic?.id, pagination.page, pagination.limit, filter]);

  const fetchStats = useCallback(async () => {
    if (!clinic?.id) return;

    try {
      const counts = await subscriptionService.getClinicSubscriptionCounts(clinic.id);
      setStats({
        total: counts.total,
        active: counts.active,
        paused: counts.paused,
        cancelled: counts.cancelled,
      });
    } catch {
      // Stats fetch failure - don't block user
    }
  }, [clinic?.id]);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleStatusUpdate = (subscriptionId: string, newStatus: string) => {
    setSubscriptions(prev =>
      prev.map(sub =>
        sub.id === subscriptionId ? { ...sub, status: newStatus as any } : sub
      )
    );
    fetchStats();
  };

  const handleViewSubscription = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setShowDetailSheet(true);
  };

  const handleCancelSubscription = (subscription: Subscription) => {
    setSubscriptionToCancel(subscription);
    setShowCancelDialog(true);
  };

  const handleCancelSuccess = () => {
    if (subscriptionToCancel) {
      handleStatusUpdate(subscriptionToCancel.id, 'cancelled');
      fetchStats();
    }
    setSubscriptionToCancel(null);
    setShowCancelDialog(false);
  };

  const handleFilterChange = (newFilter: 'all' | 'active' | 'paused' | 'cancelled') => {
    setFilter(newFilter);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, page }));
  };

  const getFilterCount = (status: string) => {
    if (status === 'all') return stats.total;
    if (status === 'cancelled') return stats.cancelled;
    if (status === 'active') return stats.active;
    if (status === 'paused') return stats.paused;
    return 0;
  };

  if (loading) {
    return (
      <div className='space-y-6'>
        {/* Header Skeleton */}
        <div className='flex flex-col justify-between gap-4 sm:flex-row sm:items-center'>
          <div className='space-y-2'>
            <Skeleton className='h-8 w-64' />
            <Skeleton className='h-4 w-96' />
          </div>
          <div className='flex gap-2'>
            <Skeleton className='h-10 w-24' />
            <Skeleton className='h-10 w-32' />
          </div>
        </div>

        {/* Stats Skeleton */}
        <div className='grid grid-cols-2 gap-4 md:grid-cols-4'>
          {[1, 2, 3, 4].map(i => (
            <div key={i} className='rounded-lg border border-slate-200 bg-white p-4'>
              <div className='flex items-center justify-between'>
                <div className='space-y-2'>
                  <Skeleton className='h-4 w-24' />
                  <Skeleton className='h-7 w-12' />
                </div>
                <Skeleton className='h-8 w-8 rounded' />
              </div>
            </div>
          ))}
        </div>

        {/* Tabs Skeleton */}
        <Skeleton className='h-10 w-80' />

        {/* Table Skeleton */}
        <div className='overflow-hidden rounded-md border border-slate-200 bg-white'>
          <div className='overflow-x-auto'>
            <table className='w-full min-w-[1000px] text-sm'>
              <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
                <tr className='border-b border-slate-200'>
                  <th className='w-12 px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700' style={{ fontSize: '0.7rem' }}>
                    #
                  </th>
                  <th className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700' style={{ fontSize: '0.7rem' }}>
                    Patient Name
                  </th>
                  <th className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700' style={{ fontSize: '0.7rem' }}>
                    Program
                  </th>
                  <th className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700' style={{ fontSize: '0.7rem' }}>
                    Medications
                  </th>
                  <th className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700' style={{ fontSize: '0.7rem' }}>
                    Frequency
                  </th>
                  <th className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700' style={{ fontSize: '0.7rem' }}>
                    Next Refill
                  </th>
                  <th className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700' style={{ fontSize: '0.7rem' }}>
                    Status & Amount
                  </th>
                  <th className='sticky right-0 w-12 px-2 py-3' style={{ background: 'linear-gradient(to right, transparent 0%, rgb(248 250 252) 20%, rgb(248 250 252) 100%)' }}></th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3, 4, 5].map(i => (
                  <tr key={i} className='border-b border-slate-100'>
                    <td className='px-4 py-3'>
                      <Skeleton className='h-4 w-8' />
                    </td>
                    <td className='px-4 py-3'>
                      <div className='flex items-center gap-3'>
                        <Skeleton className='h-8 w-8 rounded-lg' />
                        <Skeleton className='h-4 w-32' />
                      </div>
                    </td>
                    <td className='px-4 py-3'>
                      <Skeleton className='h-4 w-28' />
                    </td>
                    <td className='px-4 py-3'>
                      <Skeleton className='h-4 w-40' />
                    </td>
                    <td className='px-4 py-3'>
                      <Skeleton className='h-4 w-20' />
                    </td>
                    <td className='px-4 py-3'>
                      <Skeleton className='h-4 w-24' />
                    </td>
                    <td className='px-4 py-3'>
                      <Skeleton className='h-6 w-16' />
                    </td>
                    <td className='px-4 py-3'>
                      <Skeleton className='ml-auto h-8 w-8' />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='space-y-6'>
        <Alert variant='destructive' className='border-red-200 bg-red-50'>
          <AlertCircle className='h-4 w-4' />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <Button
          onClick={() => {
            fetchSubscriptions();
            fetchStats();
          }}
          variant='outline'
          className='gap-2'
        >
          <RefreshCw className='h-4 w-4' />
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex flex-col justify-between gap-4 sm:flex-row sm:items-center'>
        <div>
          <h1 className='font-montserrat text-xl font-bold text-slate-900 sm:text-2xl'>
            Subscription Prescriptions
          </h1>
          <p className='mt-1 text-sm text-slate-500'>
            Manage your prescription subscriptions and automatic refills. Each subscription is tied
            to a specific prescription for medical accuracy.
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <Button
            onClick={() => {
              fetchSubscriptions();
              fetchStats();
            }}
            variant='outline'
            className='gap-2 border-slate-200'
          >
            <RefreshCw className='h-4 w-4' />
            Refresh
          </Button>
          {!isGuided && (
            <Button
              onClick={() => setShowEnrollDialog(true)}
              className='gap-2 bg-brand-teal hover:bg-brand-teal/90'
            >
              <UserPlus className='h-4 w-4' />
              Enroll Patient
            </Button>
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div className='grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4'>
        <MetricsCard
          title='Total Subscriptions'
          value={stats.total}
          icon={Package}
          color='#4A8A9A'
        />
        <MetricsCard
          title='Active'
          value={stats.active}
          icon={Play}
          color='#10b981'
        />
        <MetricsCard
          title='Paused'
          value={stats.paused}
          icon={Pause}
          color='#f59e0b'
        />
        <MetricsCard
          title='Cancelled'
          value={stats.cancelled}
          icon={X}
          color='#ef4444'
        />
      </div>

      {/* Filter Tabs */}
      <div
        className='flex w-fit gap-1 rounded-lg border border-slate-200 p-1'
        style={{ backgroundColor: 'rgb(245, 247, 247)' }}
      >
        {[
          { key: 'all', label: 'All' },
          { key: 'active', label: 'Active' },
          { key: 'paused', label: 'Paused' },
          { key: 'cancelled', label: 'Cancelled' },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => handleFilterChange(tab.key as any)}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-all ${
              filter === tab.key
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {tab.label} ({getFilterCount(tab.key)})
          </button>
        ))}
      </div>

      {/* Subscriptions Table */}
      {subscriptions.length === 0 ? (
        <div className='rounded-lg border border-slate-200 bg-white py-16 text-center'>
          <div className='mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-slate-100'>
            <Package className='h-8 w-8 text-slate-400' />
          </div>
          <h3 className='mb-1 text-base font-semibold text-slate-900'>No subscriptions found</h3>
          <p className='mb-6 text-sm text-slate-500'>
            {filter === 'all'
              ? "You don't have any medication subscriptions yet."
              : `You don't have any ${filter} subscriptions.`}
          </p>
          <Button
            onClick={() => (window.location.href = '/storefront')}
            className='bg-brand-teal hover:bg-brand-teal/90'
          >
            Browse Programs
          </Button>
        </div>
      ) : (
        <div className='overflow-hidden rounded-md border border-slate-200 bg-white'>
          <div className='overflow-x-auto'>
            <table className='w-full min-w-[1000px] text-sm'>
              <thead style={{ backgroundColor: 'rgb(245, 247, 247)' }}>
                <tr className='border-b border-slate-200'>
                  <th
                    className='w-12 px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    #
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Patient Name
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Program
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Medications
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Frequency
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Next Refill
                  </th>
                  <th
                    className='px-4 py-3 text-left font-semibold uppercase tracking-wider text-slate-700'
                    style={{ fontSize: '0.7rem' }}
                  >
                    Status & Amount
                  </th>
                  <th
                    className='sticky right-0 w-12 px-2 py-3'
                    style={{
                      background:
                        'linear-gradient(to right, transparent 0%, rgb(248 250 252) 20%, rgb(248 250 252) 100%)',
                    }}
                  ></th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map((subscription, index) => (
                  <SubscriptionRow
                    key={subscription.id}
                    subscription={subscription}
                    index={((pagination.page ?? 1) - 1) * (pagination.limit ?? 10) + index}
                    onStatusUpdate={handleStatusUpdate}
                    onView={handleViewSubscription}
                    onCancel={handleCancelSubscription}
                  />
                ))}
              </tbody>
            </table>
          </div>
          {/* Pagination - matches ClinicPrescriptions pattern */}
          {subscriptions.length > 0 && (
            <div className='flex flex-col items-center gap-3 border-t border-slate-200 px-4 py-3 sm:flex-row sm:justify-between'>
              <p className='whitespace-nowrap text-xs text-slate-500'>
                Showing {(pagination.page ?? 1) * (pagination.itemsPerPage ?? 10) - (pagination.itemsPerPage ?? 10) + 1} -{' '}
                {(pagination.page ?? 1) * (pagination.itemsPerPage ?? 10) > (pagination.totalItems ?? 0)
                  ? pagination.totalItems
                  : (pagination.page ?? 1) * (pagination.itemsPerPage ?? 10)}{' '}
                of {pagination.totalItems} subscriptions
              </p>
              {pagination.totalPages && pagination.totalPages > 1 && (
                <SmartPagination
                  currentPage={pagination.page ?? 1}
                  totalPages={pagination.totalPages}
                  onPageChange={handlePageChange}
                  maxVisiblePages={3}
                  className='mx-0 justify-center sm:justify-end'
                />
              )}
            </div>
          )}
        </div>
      )}

      {/* Enroll Patient Dialog */}
      <EnrollPatientDialog
        open={showEnrollDialog}
        onOpenChange={setShowEnrollDialog}
        practitionerId={(user as any)?.is_practitioner !== false
          ? ((user as any)?.practitionerId || (user as any)?.practitioner_id || '')
          : ''}
        onSuccess={() => {
          fetchSubscriptions();
          fetchStats();
        }}
      />

      {/* Subscription Detail Sheet */}
      <SubscriptionDetailSheet
        open={showDetailSheet}
        onOpenChange={setShowDetailSheet}
        subscription={selectedSubscription}
        onUpdate={() => {
          fetchSubscriptions();
          fetchStats();
          setShowDetailSheet(false);
        }}
      />

      {/* Cancel Subscription Dialog */}
      {subscriptionToCancel && (
        <CancelSubscriptionDialog
          open={showCancelDialog}
          onOpenChange={open => {
            setShowCancelDialog(open);
            if (!open) setSubscriptionToCancel(null);
          }}
          subscriptionId={subscriptionToCancel.id}
          patientName={`${subscriptionToCancel.patient?.first_name} ${subscriptionToCancel.patient?.last_name}`}
          onSuccess={handleCancelSuccess}
        />
      )}
    </div>
  );
};

export default Subscriptions;
